import { Link } from 'react-router-dom';
import { Mail, PenLine, Users, Feather } from 'lucide-react';
import { useCopy } from '../../copy';
import { useMyLetters } from '../../hooks/useOpp';
import { buttonVariants } from '../ui/controlStyles';
import { SemanticState } from '../semantic/SemanticState';

export function OppProfileLettersTab({ sentCount }: { sentCount: number }) {
  const { t } = useCopy();
  const { data: sentLetters, isLoading } = useMyLetters('sent');

  const isEmpty = !isLoading && (sentLetters ? sentLetters.length === 0 : sentCount === 0);

  if (isEmpty) {
    return (
      <div className="space-y-6">
        <SemanticState
          illustration="empty-sent"
          kind="empty"
          title={t('profile.letters.empty.title')}
          description={t('profile.letters.empty.desc')}
          action={<Link to="/write" className={buttonVariants({ variant: 'primary', className: 'inline-flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold' })}><PenLine className="h-4 w-4" />{t('profile.letters.empty.btn')}</Link>}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Mail, title: t('profile.letters.guide.normal'), desc: t('profile.letters.guide.normal.desc') },
            { icon: Users, title: t('profile.letters.guide.drift'), desc: t('profile.letters.guide.drift.desc') },
            { icon: Feather, title: t('profile.letters.guide.future'), desc: t('profile.letters.guide.future.desc') },
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] p-4 text-center hover:shadow-sm transition-shadow">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(var(--opp-surface-raised))]">
                  <Icon className="h-5 w-5 text-[hsl(var(--opp-muted))]" />
                </div>
                <p className="text-sm font-semibold text-[hsl(var(--opp-ink))]">{item.title}</p>
                <p className="mt-1 text-xs text-[hsl(var(--opp-muted))]">{item.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[hsl(var(--opp-accent-strong))]">{t('profile.letters.recent')}</h3>
        <Link to="/letters" className="inline-flex min-h-11 items-center px-1 text-xs text-[hsl(var(--opp-muted))] underline underline-offset-2 hover:text-[hsl(var(--opp-accent-strong))]">
          {t('common.viewAll')}
        </Link>
      </div>
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="letter-card rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] p-4 flex items-start gap-3 animate-pulse">
              <div className="h-8 w-8 shrink-0 rounded-lg bg-[hsl(var(--opp-accent-soft))]" />
              <div className="flex-1 space-y-2">
                <div className="h-3 w-32 rounded bg-[hsl(var(--opp-accent-soft))]" />
                <div className="h-3 w-20 rounded bg-[hsl(var(--opp-accent-soft))]" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {(sentLetters ?? []).slice(0, 5).map((letter) => (
            <Link
              key={letter.id}
              to={`/letters?id=${letter.id}`}
              className="letter-card rounded-xl border border-[hsl(var(--opp-border))] bg-[hsl(var(--opp-surface-raised))] p-4 flex items-start gap-3 hover:shadow-sm transition-shadow"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--opp-surface-raised))]">
                <Mail className="h-4 w-4 text-[hsl(var(--opp-muted))]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[hsl(var(--opp-ink))] truncate">{letter.title || t('letters.untitled')}</p>
                <p className="text-xs text-[hsl(var(--opp-muted))] mt-0.5">{new Date(letter.created_at).toLocaleDateString()}</p>
              </div>
              <span className="shrink-0 rounded-full bg-[hsl(var(--opp-accent-soft))] px-2 py-0.5 text-caption font-medium text-[hsl(var(--opp-accent-strong))]">
                {t(`letters.status.${letter.status}`)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
